import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Tooltip, Switch, Drawer, Collapse, Card, Row, Col, Pagination, Dropdown, Menu } from "antd";
import { Link } from "react-router-dom";
import EditInfo from "./EditInfo";
import DeleteModal from "./DeleteModal";
import AddInfo from "./AddInfo";
import { getSubscriptionsList, patchSubscriptionStatus } from "../../appRedux/actions/subscriptions";
import axiosHttp from "../../appRedux/services/axiosCommon";

function ListView({ merchantCode }) {
  const dispatch = useDispatch();
  const subscriptionList = useSelector((state) => state.subscriptions.subscriptionList);
  const queryInfo = useSelector((state) => state.subscriptions.queryInfo);
  const totalRecords = useSelector((state) => state.subscriptions.totalRecords);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [platformFaqs, setPlatformFaqs] = useState([]);
  const [loadingPlatformFaqs, setLoadingPlatformFaqs] = useState(false);

  useEffect(() => {
    if (selectedSubscription) {
      if (!selectedSubscription.faqs || selectedSubscription.faqs.length === 0) {
        setLoadingPlatformFaqs(true);
        setPlatformFaqs([]);
        axiosHttp.get("/v1/admin/platforms", { params: { code: selectedSubscription.merchant } })
          .then((res) => {
            if (res.data?.status && res.data?.results?.platformList?.length > 0) {
              setPlatformFaqs(res.data.results.platformList[0].faqs || []);
            }
          })
          .catch((err) => {
            console.error("Error fetching fallback FAQs:", err);
          })
          .finally(() => {
            setLoadingPlatformFaqs(false);
          });
      } else {
        setPlatformFaqs([]);
      }
    }
  }, [selectedSubscription]);

  useEffect(() => {
    const initParams = merchantCode 
      ? { page: 1, size: 10, merchant: merchantCode } 
      : { page: 1, size: 10 };
    dispatch(getSubscriptionsList(initParams)).catch(() => {});
  }, [merchantCode]);

  const handleTableChange = (pagination) => {
    const page = pagination?.current || 1;
    const size = pagination?.pageSize || queryInfo.size;
    const fetchParams = merchantCode 
      ? { page, size, merchant: merchantCode } 
      : { ...queryInfo, page, size };
    dispatch(getSubscriptionsList(fetchParams)).catch(() => {});
  };

  const handlePageChange = (page, pageSize) => {
    handleTableChange({ current: page, pageSize });
  };

  const toggleStatus = (record) => {
    const newStatus = record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    dispatch(patchSubscriptionStatus(record._id, newStatus))
      .then(() => {
        const fetchParams = merchantCode 
          ? { page: queryInfo?.page || 1, size: queryInfo?.size || 10, merchant: merchantCode } 
          : queryInfo;
        dispatch(getSubscriptionsList(fetchParams)).catch(() => {});
      })
      .catch(() => {});
  };

  return (
    <div className="subscription-view-dominant" style={{ padding: "8px 0" }}>
      {merchantCode && (
        <div style={{ marginBottom: "20px", display: "flex", justifyContent: "flex-end" }}>
          <AddInfo merchantCode={merchantCode} />
        </div>
      )}

      {(!subscriptionList || subscriptionList.length === 0) ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888", background: "#fafafa", borderRadius: "8px" }}>
          No subscriptions found.
        </div>
      ) : (
        <Row gutter={[24, 24]}>
          {subscriptionList.map((record) => {
            const hasGlobalFeatures = record.features && record.features.length > 0;
            const hasOptionFeatures = record.pricingOptions && record.pricingOptions.some(opt => opt.features && opt.features.length > 0);
            const hasFeatures = hasGlobalFeatures || hasOptionFeatures;

            return (
              <Col xs={24} sm={24} md={12} lg={12} xl={8} key={record._id}>
                <Card 
                  hoverable 
                  style={{ borderRadius: "12px", overflow: "hidden", height: "100%", minHeight: hasFeatures ? "450px" : "auto", display: "flex", flexDirection: "column", border: "1px solid #e5e7eb" }}
                  bodyStyle={{ padding: "0", flex: 1, display: "flex", flexDirection: "column" }}
                >
                  {/* Header */}
                  <div style={{ padding: "16px", background: "#f8f9fa", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <h4 
                          onClick={() => setSelectedSubscription(record)}
                          style={{ margin: 0, fontWeight: 700, color: "#1890ff", cursor: "pointer", fontSize: "16px" }}
                          className="hover-text-primary"
                        >
                          {record.title || "—"}
                        </h4>
                        {!merchantCode && record.merchant && (
                            <Tag color="cyan" style={{ border: "none", background: "#e6fffb", color: "#006d75", borderRadius: "4px", display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "6px" }}>
                              <i className="fa fa-building-o" />
                              <span>{record.merchant}</span>
                            </Tag>
                        )}
                      </div>
                        <Dropdown 
                          menu={{
                            items: [
                              {
                                key: "edit",
                                label: <EditInfo info={record} merchantCode={merchantCode} />,
                              },
                              {
                                key: "delete",
                                label: <DeleteModal info={record} merchantCode={merchantCode} />,
                              }
                            ]
                          }} 
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <i className="material-icons" style={{ cursor: "pointer", color: "#6b7280", padding: "4px" }}>more_vert</i>
                        </Dropdown>
                    </div>
                    {record.description && (
                      <p style={{ margin: "10px 0 0", fontSize: "13px", color: "#4b5563", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: "1.5" }}>
                        {record.description}
                      </p>
                    )}
                  </div>

                  {/* Body - Pricing Options */}
                  <div style={{ padding: "16px", flex: 1, background: "#fff" }}>
                    {/* Global/Plan Features */}
                    {hasGlobalFeatures && (
                      <div style={{ marginBottom: "16px" }}>
                        <h5 style={{ fontSize: "12px", textTransform: "uppercase", color: "#9ca3af", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "8px", margin: 0 }}>
                          Plan Features
                        </h5>
                        <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#4b5563" }}>
                          {record.features.slice(0, 3).map((feat, fIdx) => (
                            <li key={fIdx} style={{ marginBottom: "3px" }}>{feat}</li>
                          ))}
                          {record.features.length > 3 && (
                            <li style={{ fontStyle: "italic", color: "#9ca3af", listStyleType: "none", marginLeft: "-16px", marginTop: "4px", fontSize: "11px" }}>
                              + {record.features.length - 3} more features
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    <h5 style={{ fontSize: "12px", textTransform: "uppercase", color: "#9ca3af", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "12px", margin: 0 }}>
                      Pricing Options
                    </h5>
                    {record.pricingOptions && record.pricingOptions.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px" }}>
                        {record.pricingOptions.map((opt, idx) => (
                          <div key={idx} style={{ display: "flex", flexDirection: "column", padding: "12px", background: "#f3f4f6", borderRadius: "8px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div style={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}>{opt.name}</div>
                                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>{opt.billingCycle} {opt.trialPeriodDays ? `· ${opt.trialPeriodDays}d trial` : ""}</div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontWeight: 700, color: "#059669", fontSize: "14px" }}>{opt.price} {opt.currency || "INR"}</div>
                              </div>
                            </div>
                            {opt.features && opt.features.length > 0 && (
                              <div style={{ marginTop: "10px", borderTop: "1px dashed #d1d5db", paddingTop: "10px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", marginBottom: "6px" }}>
                                  {opt.featuresHeader || "Features"}
                                </div>
                                <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#4b5563" }}>
                                  {opt.features.slice(0, 4).map((feat, fIdx) => (
                                    <li key={fIdx} style={{ marginBottom: "3px" }}>{feat}</li>
                                  ))}
                                  {opt.features.length > 4 && (
                                    <li style={{ fontStyle: "italic", color: "#9ca3af", listStyleType: "none", marginLeft: "-16px", marginTop: "4px", fontSize: "11px" }}>
                                      + {opt.features.length - 4} more features
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#aaa", fontSize: "13px", fontStyle: "italic", marginTop: "12px" }}>No pricing configured</div>
                    )}
                  </div>

                  {/* Footer - Status Toggle */}
                  <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fafafa", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: 500 }}>Status</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Tag color={record.status === "ACTIVE" ? "success" : record.status === "DRAFT" ? "default" : "error"} style={{ margin: 0, borderRadius: "4px", fontWeight: 600 }}>
                        {record.status}
                      </Tag>
                      <Switch 
                        size="small" 
                        checked={record.status === "ACTIVE"} 
                        onChange={() => toggleStatus(record)} 
                      />
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {totalRecords > 0 && (
        <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            current={queryInfo?.page || 1}
            pageSize={queryInfo?.size || 10}
            total={totalRecords}
            showSizeChanger
            pageSizeOptions={["5", "10", "20", "50"]}
            onChange={handlePageChange}
            showTotal={(total) => `Total ${total} subscriptions`}
          />
        </div>
      )}

      <Drawer
        title={`Subscription Plan Preview: ${selectedSubscription?.title}`}
        placement="right"
        width={600}
        onClose={() => setSelectedSubscription(null)}
        open={!!selectedSubscription}
      >
        {selectedSubscription && (
          <div>
            {/* Plan Info */}
            <div style={{ marginBottom: "20px", borderBottom: "1px solid #f0f0f0", paddingBottom: "16px" }}>
              <h3 style={{ margin: 0, fontWeight: 600, color: "#111827" }}>{selectedSubscription.title}</h3>
              <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: "14px" }}>{selectedSubscription.description || "No description provided."}</p>
              <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
                <Tag color="cyan">Merchant: {selectedSubscription.merchant}</Tag>
                <Tag color={selectedSubscription.status === "ACTIVE" ? "success" : "default"}>{selectedSubscription.status}</Tag>
              </div>
            </div>

            {/* Global Features in Preview */}
            {selectedSubscription.features && selectedSubscription.features.length > 0 && (
              <div style={{ marginBottom: "20px", borderBottom: "1px solid #f0f0f0", paddingBottom: "16px" }}>
                <h4 style={{ fontWeight: 600, marginBottom: "12px", color: "#374151", fontSize: "15px" }}>Plan Features</h4>
                <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "13px", color: "#4b5563", lineHeight: 1.6 }}>
                  {selectedSubscription.features.map((feat, fIdx) => (
                    <li key={fIdx}>{feat}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pricing Options */}
            <h4 style={{ fontWeight: 600, marginBottom: "12px", color: "#374151", fontSize: "15px" }}>Pricing & Billing Options</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "24px" }}>
              {selectedSubscription.pricingOptions?.map((opt, idx) => (
                <div key={idx} style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px 16px", background: "#f9fafb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <strong style={{ fontSize: "15px", color: "#111827" }}>{opt.name}</strong>
                    <span style={{ fontSize: "16px", fontWeight: 700, color: "#059669" }}>
                      {opt.price} {opt.currency || "INR"}
                    </span>
                  </div>
                  <div style={{ fontSize: "13px", color: "#4b5563", lineHeight: 1.6 }}>
                    <div>Billing Cycle: <strong>{opt.billingCycle}</strong> {opt.customCycleDays ? `(${opt.customCycleDays} days)` : ""}</div>
                    {opt.trialPeriodDays ? <div>Trial: <strong>{opt.trialPeriodDays} days</strong></div> : null}
                    {opt.guaranteePeriodDays ? <div>Guarantee: <strong>{opt.guaranteePeriodDays} days</strong></div> : null}
                    {opt.subtext && <div style={{ fontStyle: "italic", color: "#6b7280", marginTop: "4px" }}>{opt.subtext}</div>}
                  </div>
                  {opt.features && opt.features.length > 0 && (
                    <div style={{ marginTop: "8px", borderTop: "1px dashed #e5e7eb", paddingTop: "8px" }}>
                      <div style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" }}>
                        {opt.featuresHeader || "Option Features:"}
                      </div>
                      <ul style={{ margin: 0, paddingLeft: "16px", fontSize: "12px", color: "#374151" }}>
                        {opt.features.map((feat, fIdx) => (
                          <li key={fIdx}>{feat}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* FAQs with Fallback logic */}
            <h4 style={{ fontWeight: 600, marginBottom: "12px", color: "#374151", fontSize: "15px" }}>
              Frequently Asked Questions
            </h4>
            {selectedSubscription.faqs && selectedSubscription.faqs.length > 0 ? (
              <div>
                <div style={{ fontSize: "12px", color: "#059669", marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px", fontWeight: 500 }}>
                  <i className="fa fa-info-circle" /> Plan-Specific FAQs (Direct)
                </div>
                <Collapse accordion style={{ background: "#f9fafb" }}>
                  {selectedSubscription.faqs.map((faq, idx) => (
                    <Collapse.Panel header={<span style={{ fontWeight: 500 }}>{faq.question}</span>} key={idx}>
                      <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.5 }}>{faq.answer}</p>
                    </Collapse.Panel>
                  ))}
                </Collapse>
              </div>
            ) : loadingPlatformFaqs ? (
              <div style={{ textAlign: "center", padding: "20px 0", color: "#6b7280" }}>
                <i className="fa fa-spinner fa-spin mr-2" /> Loading fallback platform FAQs...
              </div>
            ) : platformFaqs.length > 0 ? (
              <div>
                <div style={{ fontSize: "12px", color: "#d97706", marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px", fontWeight: 500 }}>
                  <i className="fa fa-exclamation-circle" /> Fallback Global Platform FAQs (for merchant: {selectedSubscription.merchant})
                </div>
                <Collapse accordion style={{ background: "#fffbeb", borderColor: "#fef3c7" }}>
                  {platformFaqs.map((faq, idx) => (
                    <Collapse.Panel header={<span style={{ fontWeight: 500, color: "#b45309" }}>{faq.question}</span>} key={idx} style={{ background: "#fffbeb" }}>
                      <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.5 }}>{faq.answer}</p>
                    </Collapse.Panel>
                  ))}
                </Collapse>
              </div>
            ) : (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#9ca3af" }}>
                No FAQs defined (either plan-specific or platform fallback).
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default ListView;
